import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { bodyMedium, headlineSmall } from '@/styles/typography';

import type { SanityLongFormContentType } from '@/types/sanity';

export interface LongFormContentProps extends SanityLongFormContentType {}

export const LongFormContent = ({
  heading,
  subheading,
  sections,
  backgroundColour,
}: LongFormContentProps) => {
  const backgroundColourToken = backgroundColour?.token;

  return (
    <Section backgroundColour={backgroundColourToken} verticalPadding>
      <HeadingLevel>
        <HeadingContainer>
          <RichHeading heading={heading} size="headline-lg" />
          <PortableText value={subheading} />
        </HeadingContainer>
        <LongCopyContainer>
          {sections.map((section) => (
            <SectionContainer key={section._key}>
              <HeadingLevel>
                <RichHeading
                  heading={section.heading}
                  size="subheadline-bold"
                />
                <PortableText value={section.subheading} />
                {section.subsections.map((subsection) => (
                  <SubsectionContainer key={subsection._key}>
                    <HeadingLevel>
                      <RichHeading
                        heading={subsection.heading}
                        size="body-md-semi-bold"
                      />
                      <PortableText value={subsection.body} />
                    </HeadingLevel>
                  </SubsectionContainer>
                ))}
              </HeadingLevel>
            </SectionContainer>
          ))}
        </LongCopyContainer>
      </HeadingLevel>
    </Section>
  );
};

const HeadingContainer = styled.div`
  text-align: center;

  max-width: 1000px;
  margin: 0 auto;

  ${Paragraph} {
    ${headlineSmall};

    margin-top: var(--spacing-x-large);

    font-weight: normal;
  }
`;

const LongCopyContainer = styled.div`
  margin: var(--spacing-xxx-large) 0 0;
  border-radius: var(--radius-l);

  max-width: 800px;

  ${screen.sm} {
    margin: var(--spacing-xxx-large) auto 0;

    div:not(:first-of-type) {
      h3 {
        padding-top: var(--spacing-300);
      }
    }
  }

  > :not(:first-of-type) {
    h3 {
      padding-top: calc(var(--spacing-1600) * 0.8);
    }
  }
`;

const SectionContainer = styled.div`
  > p {
    margin: var(--spacing-medium) 0;
    ${bodyMedium};
  }
`;

const SubsectionContainer = styled.div`
  & + & {
    margin-top: var(--spacing-large);
  }

  p {
    ${bodyMedium};

    &:first-of-type {
      margin-top: var(--spacing-medium);
    }

    & + p {
      margin-top: var(--spacing-medium);
    }
  }
`;
